import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

import { useAbortController } from "~/hooks/useAbortController";
import { getLicenseExtensions } from "~/modules/sales/licenseExtensions/services";
import type { LicenseExtension } from "~/modules/sales/licenseExtensions/types";

type LicensesContextValue = {
	extensions: LicenseExtension[];
	loading: boolean;
	refresh: () => void;
};

const LicensesContext = createContext<LicensesContextValue | null>(null);

export const LicensesProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [extensions, setExtensions] = useState<LicenseExtension[]>([]);
	const [loading, setLoading] = useState(false);
	const { createSignal } = useAbortController();

	const fetch = useCallback((signal?: AbortSignal) => {
		setLoading(true);
		getLicenseExtensions({ page: 0, size: 100 }, signal).then((data) => {
			if (!data) return;
			setExtensions(data.data);
			setLoading(false);
		});
	}, []);

	useEffect(() => {
		const { signal, abort } = createSignal();
		fetch(signal);
		return abort;
	}, [fetch, createSignal]);

	const refresh = useCallback(() => {
		const { signal } = createSignal();
		fetch(signal);
	}, [fetch, createSignal]);

	return (
		<LicensesContext.Provider value={{ extensions, loading, refresh }}>
			{children}
		</LicensesContext.Provider>
	);
};

export function useLicenses() {
	const ctx = useContext(LicensesContext);
	if (!ctx) throw new Error("useLicenses must be used within LicensesProvider");
	return ctx;
}
